import { UseMutationResult } from "@tanstack/react-query";

export enum ImageUploadType {
  AVATAR = "avatar",
  USER_COVER = "user_cover",
  COLLECTION_COVER = "collection_cover",
  LOOK_COVER = "look_cover",
  PRODUCT_IMAGE = "product_image",
  FEEDBACK_IMAGE = "feedback_image",
  TRY_ON_PHOTO = "try_on_photo",
}

export interface PresignResponse {
  uploadUrl: string;
  publicUrl: string;
  key: string;
}

export interface PresignRequest {
  fileName: string;
  contentType: string;
}

export interface CropConfig {
  aspectRatio: number;
  shape: "rect" | "round";
  required: boolean;
  autoCrop?: boolean;
  title: string;
}

export interface UploadTypeConfig {
  compressionPreset: any; // PWA-specific compression config
  presignHook: () => UseMutationResult<PresignResponse, Error, PresignRequest>;
  cropConfig: CropConfig;
  defaultFileName: string;
}

export enum UploadState {
  IDLE = "idle",
  FILE_PICKER_OPEN = "file_picker_open",
  PROCESSING = "processing", // For HEIC conversion, validation, etc.
  CROPPING = "cropping",
  UPLOADING = "uploading",
  AUTO_SAVING = "auto_saving",
  SUCCESS = "success",
  ERROR = "error",
}

export interface AutoSaveConfig<T = any> {
  mutation: UseMutationResult<T, Error, { id: string; data: any }>;
  entityId: string;
  field: string;
  onSuccess?: (url: string) => void;
  onError?: (error: Error) => void;
}

export interface UseImageUploadOptions {
  type: ImageUploadType;
  initialPreview?: string;
  autoSave?: AutoSaveConfig;
  onSuccess?: (publicUrl: string) => void;
  onError?: (error: string) => void;
  customCropping?: Partial<CropConfig>;
}

export interface FilePickerProps {
  open: boolean;
  onClose: () => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove?: () => void;
  showRemoveButton: boolean;
}

export interface CropperProps {
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  onCropComplete: (blob: Blob) => void;
  aspectRatio: number;
  cropShape: "rect" | "round";
  title: string;
}

export interface UseImageUploadReturn {
  // Current state
  previewUrl: string;
  isUploading: boolean;
  uploadError: string | null;
  currentState: UploadState;

  // Actions
  triggerUpload: () => void;
  clearPreview: () => void;
  clearError: () => void;
  handleDirectFile: (file: File) => Promise<boolean>;

  // Ready-to-use bottom sheet props
  filePickerProps: FilePickerProps;
  cropperProps: CropperProps;
}
